/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 */

/**
 * Retry Utility with Exponential Backoff
 *
 * Provides configurable retry logic with exponential backoff and jitter
 * to handle transient failures gracefully.
 */

export interface RetryConfig {
  maxAttempts: number;
  baseDelay: number;
  maxDelay: number;
  backoffMultiplier: number;
  jitter: boolean;
  retryableErrors?: (error: Error) => boolean;
}

export const RETRY_CONFIGS = {
  DEFAULT: {
    maxAttempts: 3,
    baseDelay: 1000,
    maxDelay: 10000,
    backoffMultiplier: 2,
    jitter: true,
  },
  DATABASE: {
    maxAttempts: 3,
    baseDelay: 500,
    maxDelay: 5000,
    backoffMultiplier: 2,
    jitter: true,
  },
  EXTERNAL: {
    maxAttempts: 5,
    baseDelay: 1000,
    maxDelay: 30000,
    backoffMultiplier: 2,
    jitter: true,
  },
  AI_SERVICES: {
    maxAttempts: 2, // Don't retry expensive AI calls too much
    baseDelay: 2000,
    maxDelay: 10000,
    backoffMultiplier: 1.5,
    jitter: true,
  },
} as const satisfies Record<string, RetryConfig>;

/**
 * Check if an error is retryable based on common patterns
 */
export function isRetryableError(error: Error): boolean {
  const message = error.message.toLowerCase();

  // Network errors
  if (
    message.includes("network") ||
    message.includes("timeout") ||
    message.includes("econnreset") ||
    message.includes("econnrefused") ||
    message.includes("socket")
  ) {
    return true;
  }

  // Rate limiting (429)
  if (message.includes("429") || message.includes("rate limit")) {
    return true;
  }

  // Server errors (5xx)
  if (message.includes("50") || message.includes("server error")) {
    return true;
  }

  // Temporary database errors
  if (
    message.includes("connection") ||
    message.includes("deadlock") ||
    message.includes("lock timeout")
  ) {
    return true;
  }

  // Don't retry client errors (4xx) except 429
  if (message.includes("40") && !message.includes("429")) {
    return false;
  }

  // Don't retry authentication errors
  if (message.includes("401") || message.includes("unauthorized")) {
    return false;
  }

  // Don't retry permission errors
  if (message.includes("403") || message.includes("forbidden")) {
    return false;
  }

  // Default: retry if uncertain
  return true;
}

/**
 * Retry an operation with exponential backoff
 */
export async function retryWithBackoff<T>(
  operation: () => Promise<T>,
  config: RetryConfig = RETRY_CONFIGS.DEFAULT,
  onRetry?: (attempt: number, error: Error) => void,
): Promise<T> {
  let lastError: Error;

  for (let attempt = 1; attempt <= config.maxAttempts; attempt++) {
    try {
      return await operation();
    } catch (error) {
      lastError = error instanceof Error ? error : new Error(String(error));

      // Check if error is retryable
      const isRetryable = config.retryableErrors
        ? config.retryableErrors(lastError)
        : isRetryableError(lastError);

      if (!isRetryable) {
        throw lastError;
      }

      // Don't retry on last attempt
      if (attempt === config.maxAttempts) {
        throw lastError;
      }

      // Calculate delay with exponential backoff
      let delay =
        config.baseDelay * Math.pow(config.backoffMultiplier, attempt - 1);
      delay = Math.min(delay, config.maxDelay);

      // Add jitter to prevent thundering herd
      if (config.jitter) {
        delay = delay * (0.5 + Math.random() * 0.5);
      }

      onRetry?.(attempt, lastError);
      await new Promise((resolve) => setTimeout(resolve, Math.floor(delay)));
    }
  }

  throw lastError!;
}
